import type { ReactNode } from "react";
import { useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";

export interface AiSuggestionCardProps {
  /** The proposal itself (message, chips, missing items...). */
  children: ReactNode;
  /** Evidence line shown after "Porque:". */
  because: string;
  /** Replaces the default primary button (e.g. the WhatsApp "Enviar" link). */
  primary?: ReactNode;
  /** Label of the default primary button. Defaults to "Enviar". */
  primaryLabel?: string;
  onPrimary?: () => void;
  onEdit: () => void;
  onDismiss: () => void;
  /** Disables the three outputs while a decision is being saved. */
  busy?: boolean;
}

const OUTPUT_BUTTON_CLASSES = "h-11 min-w-11 px-4 active:scale-[0.98]";

/**
 * Almikia Epic EI (TASK-004): the shared "Tarjeta de sugerencia IA"
 * (design-system.md section 3/8): gradient border (the only place besides the
 * ring, month bar and primary button where the gradient is allowed), the
 * review label, the proposal, a "Porque:" evidence line and the three
 * outputs Enviar / Editar / Ahora no. The AI never acts alone: every
 * output is a person's tap.
 */
export function AiSuggestionCard({
  children,
  because,
  primary,
  primaryLabel,
  onPrimary,
  onEdit,
  onDismiss,
  busy = false,
}: AiSuggestionCardProps) {
  const translate = useTranslate();

  return (
    <div
      role="region"
      aria-label={translate("crm.ai.card_aria", { _: "Sugerencia de IA" })}
      className="rounded-[14px] bg-[image:var(--gradient-brand-button)] p-[2px]"
    >
      <div className="flex flex-col gap-3 rounded-[12px] bg-white p-4">
        <p className="text-xs font-semibold text-[#6B6F8A]">
          {translate("crm.ai.label", {
            _: "✦ Sugerencia · revísala antes de enviar",
          })}
        </p>
        <div className="text-[15px] text-[#20244A]">{children}</div>
        <p className="text-sm text-[#6B6F8A]">
          <span className="font-semibold">
            {translate("crm.ai.because", { _: "Porque:" })}
          </span>{" "}
          {because}
        </p>
        <div className="flex flex-wrap gap-2">
          {primary ?? (
            <Button
              type="button"
              variant="gradient"
              className={OUTPUT_BUTTON_CLASSES}
              disabled={busy}
              onClick={onPrimary}
            >
              {primaryLabel ?? translate("crm.ai.send", { _: "Enviar" })}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            className={OUTPUT_BUTTON_CLASSES}
            disabled={busy}
            onClick={onEdit}
          >
            {translate("crm.ai.edit", { _: "Editar" })}
          </Button>
          <Button
            type="button"
            variant="outline"
            className={OUTPUT_BUTTON_CLASSES}
            disabled={busy}
            onClick={onDismiss}
          >
            {translate("crm.ai.dismiss", { _: "Ahora no" })}
          </Button>
        </div>
      </div>
    </div>
  );
}
